import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import AuthShell from '../../components/layout/AuthShell';
import Btn from '../../components/ui/Btn';
import { Input, Spinner, ComingSoon } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import ForgotPasswordModal from '../../components/auth/ForgotPasswordModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';

export default function SignInScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  // If already signed in, jump to hub (or wherever they were trying to go)
  useEffect(() => {
    if (!authLoading && session) {
      // Preserve the full return location incl. query string (e.g. /hub?invite=<id>)
      // so an invite link survives the bounce through sign-in.
      const from = location.state?.from;
      const dest = from ? (from.pathname || '/hub') + (from.search || '') : '/hub';
      navigate(dest, { replace: true });
    }
  }, [session, authLoading, navigate, location]);

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError(null);

    if (!email.trim() || !pw) {
      setError('Please enter your email and password.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: pw,
      });
      if (signInError) throw signInError;
      // Navigation handled by the useEffect above when session arrives
    } catch (err) {
      console.error('Sign in failed:', err);
      setError(err.message || 'Sign in failed. Check your credentials and try again.');
      setSubmitting(false);
    }
  };

  return (
    <AuthShell side={{
      topRight: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-3)' }}>New to MagicIntake?</span>
          <Link to="/register" style={{ textDecoration: 'none' }}>
            <Btn variant="outline" size="sm">Create workspace</Btn>
          </Link>
        </div>
      ),
    }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', marginBottom: 6 }}>
          Welcome back
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-3)' }}>
          Sign in to keep your intake moving.
        </p>
      </div>

      {/* Google SSO (Coming soon) */}
      <Btn
        variant="outline"
        size="md"
        style={{ width: '100%', marginBottom: 20, position: 'relative' }}
        disabled
        title="Coming soon"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" style={{ marginRight: 4 }}>
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
        <span style={{ position: 'absolute', top: -8, right: -8 }}>
          <ComingSoon />
        </span>
      </Btn>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 20px',
        color: 'var(--text-muted)', fontSize: 11,
      }}>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        OR CONTINUE WITH EMAIL
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>

      {error && <ErrorBanner message={error} style={{ marginBottom: 14 }} />}

      <form onSubmit={submit} noValidate>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
          Work email
        </label>
        <Input
          icon={Icon.mail}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          style={{ marginBottom: 14 }}
          disabled={submitting}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
            Password
          </label>
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: 12, color: 'var(--accent-600)', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Forgot password?
          </button>
        </div>
        <Input
          icon={Icon.lock}
          type="password"
          autoComplete="current-password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          style={{ marginBottom: 16 }}
          disabled={submitting}
        />

        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
          color: 'var(--text-2)', marginBottom: 18, cursor: 'pointer',
        }}>
          <input type="checkbox" defaultChecked style={{ accentColor: '#3dc75b', width: 15, height: 15 }} />
          Keep me signed in for 30 days
        </label>

        <Btn
          type="submit"
          variant="primary"
          size="lg"
          style={{ width: '100%' }}
          iconRight={submitting ? null : Icon.arrowR}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Signing you in…</> : 'Sign in securely'}
        </Btn>
      </form>

      <div style={{
        marginTop: 24, padding: 12, background: 'var(--surface-2)',
        border: '1px solid var(--border)', borderRadius: 10,
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 12, color: 'var(--text-2)',
      }}>
        <Icon.shield size={16} stroke="var(--accent-600)" />
        SOC 2 Type II · HIPAA · GDPR · 256-bit encryption at rest
      </div>

      <ForgotPasswordModal
        open={forgotOpen}
        initialEmail={email}
        onClose={() => setForgotOpen(false)}
      />
    </AuthShell>
  );
}

export function ErrorBanner({ message, style }) {
  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--danger-bg)',
      border: '1px solid rgba(239,68,68,0.22)',
      borderRadius: 10,
      color: 'var(--danger)',
      fontSize: 12.5,
      lineHeight: 1.45,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      ...style,
    }}>
      <Icon.alert size={14} style={{ flexShrink: 0, marginTop: 2 }} />
      <span>{message}</span>
    </div>
  );
}
